<template>
  <v-container fluid>
    <CustomersAssetsSearch @search="() => $refs.table.refresh()" />

    <BaseTable
      ref="table"
      nowrap-headers
      title="Gestione Assets"
      :headers="headers"
      :actions="actions"
      module="customersAssets"
      :exportable="canUser('customers.assets', 'download')"
    >
    <template v-slot:item.customer.common_name="{ item }">
        <router-link
          class="customer_name text-decoration-none d-inline-flex align-center"
          :to="{ name: 'customers_detail', params: { id: item.customer_id } }"
        >
          <strong class="font-weight-bold">
            {{ item | get(['customer', 'common_name'], '') }}
          </strong>
        </router-link>
      </template>
      <template v-slot:item.customers_user="{ item }">
        <span v-if="item.customers_user">
          {{ item.customers_user.last_name }}
          {{ item.customers_user.first_name }}
        </span>
        <em v-else>Non specificato</em>
      </template>

      <template v-slot:item.plans_subscription.code="{ item }">
        <v-chip label>{{ item.plans_subscription.code }}</v-chip>
      </template>
      <template v-slot:item.serial_code="{ item }">
        <v-chip label outlined pill>{{ item.serial_code | placeholder('-') }}</v-chip>
      </template>
      <template v-slot:item.imei="{ item }">
        <v-chip label outlined pill>{{ item.imei | placeholder('-') }}</v-chip>
        <div v-if="item.imei2" class="mt-1">
          <v-chip label outlined pill>{{ item.imei2 }}</v-chip>
        </div>
      </template>
      <template v-slot:item.contract_id="{ item }">
        <v-chip label outlined pill color="primary">{{ item.contract_id | placeholder('-') }}</v-chip>
      </template>
      <template v-slot:item.assigned_at="{ item }">
        {{ item.assigned_at | momentOr('DD/MM/YYYY', '-') }}
      </template>
      <template v-slot:item.contract_start_at="{ item }">
        {{ item.contract_start_at | momentOr('DD/MM/YYYY', '-') }}
      </template>
      <template v-slot:item.contract_end_at="{ item }">
        {{ item.contract_end_at | momentOr('DD/MM/YYYY', '-') }}
      </template>
      <template v-slot:item.has_kasko="{ item }">
        {{ item.has_kasko ? 'SÌ' : 'NO' }}
      </template>
      <template v-slot:item.associated="{ item }">
        <template v-if="item.associated">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on">
                mdi-devices
              </v-icon>
            </template>

            <v-card>
              <v-card-title class="text-h6">Asset associato</v-card-title>
              <v-card-text>
                <strong>
                  {{ item.associated.product.brand }}
                  {{ item.associated.product.model }}
                </strong>
                <br />
                <small>{{ item.associated.product.type }}</small>
                <br />
                <v-chip label small v-if="item.associated.serial_code">SN: {{ item.associated.serial_code }}</v-chip>
                <br />
                <v-chip label small v-if="item.associated.imei">IMEI: {{ item.associated.imei }}</v-chip>
                
                <br />
                <br />
                <small>Stato:</small><br>
                <v-chip label small color="primary">{{ item.associated.status }}</v-chip>
              </v-card-text>
            </v-card>
          </v-menu>
        </template>
        <em v-else>-</em>
      </template>
      <template v-slot:footer>
        <v-dialog
          v-model="dialog"
          max-width="75%"
          :fullscreen="$vuetify.breakpoint.mobile"
          :retain-focus="false"
          @click:outside="modalClose"
        >
          <v-card>
            <v-card-title>Asset Cliente</v-card-title>
            <v-card-text>
              <CustomerAssetsForm
                v-if="dialog"
                :edit="editMode"
                @submitted="onSubmitted"
              />
            </v-card-text>
          </v-card>
        </v-dialog>
      </template>
    </BaseTable>
  </v-container>
</template>

<script>
import CustomersAssetsSearch from '@components/customer_assets/CustomersAssetsSearch.vue'
import BaseTable from '@components/structure/base-table.vue'
import { mapActions, mapMutations } from 'vuex'
import { authComputed } from '@state/helpers.js'
import CustomerAssetsForm from '@components/customer_assets/CustomerAssetsForm.vue'

export default {
  name: 'AssetsSearch',
  components: { CustomersAssetsSearch, BaseTable, CustomerAssetsForm },
  page: {
    title: 'Ricerca',
  },
  data() {
    return {
      dialog: false,
      menu: false,
      editMode: false,
      headers: [
        {
          text: 'Cliente',
          value: 'customer.common_name',
        },
        {
          text: 'Sottoscrizione',
          value: 'plans_subscription.code',
        },
        {
          text: 'Utente',
          value: 'customers_user',
        },
        {
          text: 'Stato',
          value: 'status',
        },
        {
          text: 'Brand',
          align: 'start',
          value: 'brand',
        },
        {
          text: 'Modello',
          value: 'model',
        },
        {
          text: 'Seriale',
          value: 'serial_code',
        },
        {
          text: 'IMEI/MSISDN',
          value: 'imei',
        },
        {
          text: 'ICCID',
          value: 'iccid',
        },
        {
          text: 'Telefono',
          value: 'phone_number',
        },
        {
          text: 'Contratto',
          value: 'contract_id',
        },
        {
          text: 'Kasko',
          value: 'has_kasko',
        },
        {
          text: 'Assegnazione',
          value: 'assigned_at',
        },
        {
          text: 'Inserimento',
          value: 'created_at',
        },
        {
          text: 'Asset associato',
          value: 'associated',
        },
      ],
      actions: [
        {
          label: 'Mostra dettagli',
          icon: 'mdi-magnify',
          color: 'primary',
          to: (item) => ({ name: 'assets_detail', params: { id: item.id } }),
          onItemCondition: () => this.canUser('customers.assets', 'read'),
        },
        {
          label: 'Modifica',
          icon: 'mdi-pencil',
          color: 'primary',
          onItemCondition: () => this.canUser('customers.assets', 'update'),
          handler: this.openModal,
        },
        {
          label: 'Elimina asset',
          icon: 'mdi-delete',
          color: 'red',
          onItemCondition: () => this.canUser('customers.assets', 'delete'),
          handler: this.remove,
        },
      ],
    }
  },
  computed: {
    ...authComputed,
  },
  mounted() {
    this.setScopes(['customer', 'product', 'customer_user', 'subscription', 'associated'])
  },
  beforeDestroy() {
    this.setScopes([])
  },
  methods: {
    ...mapActions('customersAssets', ['removeItem']),
    ...mapMutations('customersAssets', {
      setScopes: 'SET_SCOPES',
      setCurrent: 'SET_CURRENT',
      resetCurrent: 'RESET_CURRENT',
    }),

    openModal(item) {
      /* Allow for editing and creating new items (not currently used) */
      const startingPoint = item.id ? item : {}
      // Understand if the modal was opened as editing or for a new sub
      const isEditing = !!startingPoint.id
      this.editMode = isEditing
      this.setCurrent(startingPoint)
      this.dialog = true
    },
    onSubmitted() {
      this.modalClose()
      this.$refs.table.refresh()
    },
    modalClose() {
      this.dialog = false
      this.editMode = false
      this.resetCurrent()
    },

    async remove(item) {
      const res = await this.$dialog.confirm({
        text:
          "Proseguire con l'eliminazione dell'asset ?\nL'azione non è reversibile!",
        title: 'Attenzione',
      })

      if (!res) return

      await this.removeItem(item.id)
      this.$refs.table.refresh()
    },
  },
}
</script>

<style scoped></style>
